/**
 * Product-level WMS Weight API for collection / search product grids.
 * One POST /public/products/weights/products-batch per parent product (not one
 * mega-batch for the whole result set), with a small concurrency limit.
 * Complete response cached in window.AOneProductWeights for ATC / QV / weight modal.
 */

const DEBOUNCE_MS = 300;
const MAX_DEBOUNCE_WAIT_MS = 2000;
const WMS_REQUEST_TIMEOUT_MS = 20000;
/** Max simultaneous per-parent products-batch POSTs (Collection / SLP / shared). */
const MAX_CONCURRENT_PRODUCT_REQUESTS = 5;
const EMPTY_DISPLAY = 'N/A';
/** Meta fields (min weight / available qty) while the WMS request is pending. */
const META_LOADING_DISPLAY = 'Checking...';
/** Add-to-cart button while the WMS request is pending. */
const BUTTON_LOADING_DISPLAY = 'Checking availability...';
/** @deprecated Use META_LOADING_DISPLAY / BUTTON_LOADING_DISPLAY — kept for callers. */
const LOADING_DISPLAY = BUTTON_LOADING_DISPLAY;
const COMING_SOON_LABEL = 'COMING SOON';
const ADD_TO_CART_LABEL = 'ADD TO CART';

const CARD_SELECTOR =
  '.plp-card[data-product-id], .inventory-product-row[data-product-id]';

const GRID_ROOT_SELECTORS = [
  '.plp-product-grid',
  '[data-testid="product-grid"]',
];

const IGNORE_SELECTOR =
  '.predictive-search, .wizzy-autocomplete-wrapper, .wizzy-search-results-list, .wizzy-search-wrapper, .quick-view, #quick-view-modal, #QuickViewModal';

/** @type {string} */
let activeRequestKey = '';

/** @type {Promise<unknown[]> | null} */
let activeRequestPromise = null;

/** @type {string} */
let lastAppliedRequestKey = '';

/** Monotonic generation — supersede without AbortController.cancel (avoids Network "canceled"). */
let requestGeneration = 0;

/**
 * Concurrent products-batch protection: productId → in-flight per-parent promise.
 * Cleared in finally when that request settles. Not a permanent fetched cache.
 * @type {Map<string, Promise<unknown[]>>}
 */
const inFlightByProductId = new Map();

/**
 * FIFO queue of parent product IDs waiting for a products-batch POST.
 * Each entry becomes its own `{ productIds: [id] }` request when a concurrency slot opens.
 * @type {Array<{ id: string, source: string, resolveOuter: (value: unknown[]) => void }>}
 */
const pendingProductRequestQueue = [];

/** Currently running per-parent products-batch POSTs. */
let activeProductRequestCount = 0;

/** @type {ReturnType<typeof setTimeout> | null} */
let debounceTimer = null;

/** @type {number} */
let debounceStartedAt = 0;

/** @type {MutationObserver | null} */
let gridObserver = null;

/** @type {boolean} */
let initialized = false;

/** @type {boolean} */
let isApplyingDom = false;

/** @type {ParentNode} */
let pendingHydrateScope = document;

/** @type {string} */
let pendingHydrateSource = 'collection-init';

/** Last product-id fingerprint observed in the grid (skip no-op rehydrates). */
let lastSeenMembershipKey = '';

/**
 * @typedef {{
 *   enabled: boolean,
 *   baseUrl: string,
 *   productsBatchPath: string,
 *   timeoutMs: number,
 *   debug: boolean,
 * }} ProductWeightConfig
 */

function ensureStore() {
  const w = /** @type {any} */ (window);
  if (!w.AOneProductWeights) {
    w.AOneProductWeights = {
      products: new Map(),
      skus: new Map(),
      variants: new Map(),
      activeRequest: null,
      activeRequestKey: null,
      abortController: null,
      currentRenderedProductIds: new Set(),
      status: 'idle',
      lastError: null,
    };
  }
  return w.AOneProductWeights;
}

/**
 * Shared SKU raw cache used by quick-view-modal.js / plp-table-details.js.
 * @returns {{
 *   rawBySku: Record<string, unknown>,
 *   dataBySku: Record<string, unknown>,
 *   pendingBySku: Record<string, Promise<void>>,
 *   pendingBatchByKey: Record<string, Promise<void>>,
 * }}
 */
function ensureSharedWeightStore() {
  const w = /** @type {any} */ (window);
  if (!w.__aoneWeightSharedStore) {
    w.__aoneWeightSharedStore = {
      rawBySku: Object.create(null),
      dataBySku: Object.create(null),
      pendingBySku: Object.create(null),
      pendingBatchByKey: Object.create(null),
    };
  }
  return w.__aoneWeightSharedStore;
}

function getWmsAvailabilityApi() {
  return (
    /** @type {any} */ (window).WMSInventory ||
    /** @type {any} */ (window).AOneWmsAvailability ||
    null
  );
}

/**
 * @returns {ProductWeightConfig}
 */
function getWmsConfiguration() {
  const cfg = /** @type {any} */ (window).AOneWmsConfig;
  const baseUrl = String(cfg?.apiBaseUrl || '')
    .trim()
    .replace(/\/+$/, '');
  const productsBatchPath = String(
    cfg?.endpoints?.productsBatch || '/public/products/weights/products-batch'
  ).trim();
  const enabled = cfg?.enabled !== false && Boolean(baseUrl);
  const timeoutMs = Math.max(1000, Number(cfg?.timeoutMs) || WMS_REQUEST_TIMEOUT_MS);
  const debug = Boolean(cfg?.debug);

  // Legacy JSON nodes as fallback when AOneWmsConfig is missing (older caches).
  if (!baseUrl) {
    const legacy = readLegacyConfig();
    if (legacy.baseUrl) {
      return {
        enabled: legacy.enabled,
        baseUrl: legacy.baseUrl,
        productsBatchPath,
        timeoutMs: legacy.timeoutMs || timeoutMs,
        debug: legacy.debug || debug,
      };
    }
  }

  return { enabled, baseUrl, productsBatchPath, timeoutMs, debug };
}

/**
 * @returns {{ enabled: boolean, baseUrl: string, timeoutMs: number, debug: boolean }}
 */
function readLegacyConfig() {
  const fallback = { enabled: false, baseUrl: '', timeoutMs: WMS_REQUEST_TIMEOUT_MS, debug: false };
  const candidates = [
    document.querySelector('[data-batch-weight-config]'),
    document.querySelector('[data-theme-weight-config], [data-plp-fetch-weight-config-section]'),
    document.querySelector('#QuickViewModal [data-plp-fetch-weight-config], [data-quick-view-weight-config]'),
    document.querySelector('[data-plp-fetch-weight-config]'),
  ];

  for (const el of candidates) {
    if (!el || !el.textContent) continue;
    try {
      const parsed = JSON.parse(el.textContent || '{}');
      if (!parsed || typeof parsed !== 'object') continue;
      if (el.hasAttribute('data-batch-weight-config') && parsed.enabled === false) continue;
      const baseUrl = String(parsed.baseUrl || parsed.apiBaseUrl || '')
        .trim()
        .replace(/\/+$/, '');
      if (!baseUrl) continue;
      return {
        enabled: parsed.enabled === undefined ? true : Boolean(parsed.enabled),
        baseUrl,
        timeoutMs: Math.max(1000, Number(parsed.timeoutMs) || WMS_REQUEST_TIMEOUT_MS),
        debug: Boolean(parsed.debug),
      };
    } catch {
      /* try next */
    }
  }
  return fallback;
}

/** @param {...unknown} args */
function debugLog(...args) {
  const cfg = getWmsConfiguration();
  if (!cfg.debug) return;
  console.log('[AOneProductWeights]', ...args);
}

/** @param {string} base @param {string} path */
function joinUrl(base, path) {
  const b = String(base || '').replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  if (!b) return '';
  if (!p) return b;
  if (/^https?:\/\//i.test(p)) return p;
  return `${b}/${p}`;
}

function getProductsBatchUrl() {
  const { baseUrl, productsBatchPath } = getWmsConfiguration();
  if (!baseUrl) {
    throw new Error(
      '[A-One WMS] API base URL is missing. Configure it in Shopify Theme settings → WMS API Integration.'
    );
  }
  if (!productsBatchPath) {
    throw new Error('[A-One WMS] Products batch endpoint path is missing.');
  }
  return joinUrl(baseUrl, productsBatchPath);
}

/** @param {string} url */
function requestHeaders(url) {
  /** @type {Record<string, string>} */
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  try {
    const host = new URL(url, window.location.origin).hostname.toLowerCase();
    if (host.includes('ngrok')) headers['ngrok-skip-browser-warning'] = 'true';
  } catch {
    /* ignore */
  }
  return headers;
}

/** @param {unknown} value */
function normalizeShopifyId(value) {
  if (value === null || value === undefined) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  if (stringValue.includes('/')) return stringValue.split('/').pop() || '';
  return stringValue;
}

/** @param {string[]} productIds */
function createProductRequestKey(productIds) {
  return [...productIds]
    .map(normalizeShopifyId)
    .filter(Boolean)
    .sort()
    .join(',');
}

/** @param {unknown} value */
function toFiniteNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * @typedef {{
 *   status: 'loading' | 'available' | 'coming_soon' | 'all_in_cart',
 *   isLoading: boolean,
 *   isLoaded: boolean,
 *   hasError: boolean,
 *   availableQty: number | null,
 *   minWeight: number | null,
 *   unit: string | null,
 *   isWmsSoldOut: boolean,
 *   selectedSku: string,
 *   selectedSkuQty: number | null,
 *   selectedSkuMatched: boolean,
 *   canAddToCart: boolean,
 *   weightText: string,
 *   qtyText: string,
 *   buttonText: string,
 *   disabled: boolean,
 * }} ProductInventoryState
 */

/**
 * @param {unknown} weight
 * @param {string} [unit]
 */
function formatWeight(weight, unit = 'g') {
  const number = Number(weight);
  if (!Number.isFinite(number)) return EMPTY_DISPLAY;
  return `${number.toFixed(3)} ${unit || 'g'}`;
}

/**
 * @param {number} value
 * @returns {string}
 */
function formatWeightCompact(value) {
  const trimmed = String(value)
    .replace(/\.0+$/, '')
    .replace(/(\.\d*?)0+$/, '$1');
  return trimmed;
}

/**
 * @param {{
 *   isLoading: boolean,
 *   minWeight: number | null,
 *   unit?: string | null
 * }} args
 */
function formatMinWeightDisplay({ isLoading, minWeight, unit }) {
  if (isLoading) return META_LOADING_DISPLAY;
  if (!Number.isFinite(minWeight) || minWeight === null || minWeight <= 0) return EMPTY_DISPLAY;
  const safeUnit = String(unit || 'g').trim() || 'g';
  return `${formatWeightCompact(minWeight)}${safeUnit}`;
}

/**
 * @param {unknown} rawQty
 * @returns {number | null}
 */
function normalizeQuantity(rawQty) {
  const parsed = toFiniteNumber(rawQty);
  if (!Number.isFinite(parsed) || parsed === null) return null;
  return Math.max(0, Math.floor(parsed));
}

/**
 * @param {unknown} rawMinWeight
 * @returns {number | null}
 */
function normalizeMinWeight(rawMinWeight) {
  const parsed = toFiniteNumber(rawMinWeight);
  if (!Number.isFinite(parsed) || parsed === null || parsed <= 0) return null;
  return parsed;
}

/**
 * @param {HTMLElement} card
 * @returns {string}
 */
function resolveCardSelectedSku(card) {
  const direct = String(card.dataset.variantSku || card.dataset.weightSku || '').trim();
  if (direct) return direct;
  const skuInput = /** @type {HTMLInputElement | null} */ (card.querySelector('input[name="sku"]'));
  if (skuInput?.value) return String(skuInput.value).trim();
  const variantIdInput = /** @type {HTMLInputElement | null} */ (card.querySelector('input[name="id"]'));
  const variantId = normalizeShopifyId(variantIdInput?.value);
  if (!variantId) return '';
  const store = ensureStore();
  const variantEntry = store.variants.get(variantId);
  return String(variantEntry?.sku || '').trim();
}

/**
 * @param {Record<string, unknown>} product
 * @param {string} sku
 * @returns {{ matched: boolean, count: number | null }}
 */
function getSkuAvailabilityFromProduct(product, sku) {
  const safeSku = String(sku || '').trim();
  if (!safeSku) return { matched: false, count: null };
  const list = Array.isArray(product.skus)
    ? product.skus
    : Array.isArray(product.items)
      ? product.items
      : [];
  const skuEntry = list.find((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    return String(/** @type {Record<string, unknown>} */ (entry).sku || '').trim() === safeSku;
  });
  if (!skuEntry || typeof skuEntry !== 'object') return { matched: false, count: null };
  const skuObj = /** @type {Record<string, unknown>} */ (skuEntry);
  if (skuObj.found === false) return { matched: true, count: 0 };
  const count = normalizeQuantity(
    skuObj.count ??
      skuObj.availableCount ??
      skuObj.availableQuantity ??
      skuObj.quantity ??
      skuObj.qty
  );
  if (count === null) return { matched: true, count: null };
  return { matched: true, count };
}

/**
 * Normalize Product Batch API product → single card availability state.
 * Source of truth: summary.totalAvailableQuantity only (never Shopify inventory).
 * Final states after response: available | coming_soon (never error / sold out).
 *
 * @param {Record<string, unknown> | null | undefined} product
 * @param {{ forceLoading?: boolean, forceError?: boolean, card?: HTMLElement | null }} [options]
 * @returns {ProductInventoryState}
 */
function normalizeWmsAvailability(product, options = {}) {
  const card = options.card instanceof HTMLElement ? options.card : null;
  const selectedSku = card ? resolveCardSelectedSku(card) : '';
  const forceLoading = options.forceLoading === true;

  if (forceLoading) {
    return {
      status: 'loading',
      isLoading: true,
      isLoaded: false,
      hasError: false,
      availableQty: null,
      minWeight: null,
      unit: null,
      isWmsSoldOut: false,
      selectedSku,
      selectedSkuQty: null,
      selectedSkuMatched: false,
      canAddToCart: false,
      weightText: META_LOADING_DISPLAY,
      qtyText: META_LOADING_DISPLAY,
      buttonText: BUTTON_LOADING_DISPLAY,
      disabled: true,
    };
  }

  const summary =
    product && product.summary && typeof product.summary === 'object'
      ? /** @type {Record<string, unknown>} */ (product.summary)
      : {};
  const minWeightOption =
    summary.minimumWeightOption && typeof summary.minimumWeightOption === 'object'
      ? /** @type {Record<string, unknown>} */ (summary.minimumWeightOption)
      : null;

  // Prefer exact Product Batch field; keep legacy aliases as defensive fallbacks only.
  const totalAvailableQuantity = Number(
    summary.totalAvailableQuantity ??
      summary.totalAvailableCount ??
      summary.availableCount ??
      0
  );
  const rawQuantity = Number.isFinite(totalAvailableQuantity)
    ? Math.max(0, Math.floor(totalAvailableQuantity))
    : 0;

  const availabilityApi = getWmsAvailabilityApi();
  const productId = product ? normalizeShopifyId(product.productId) : '';
  let quantity = rawQuantity;
  let cartAwareMinWeight = normalizeMinWeight(
    summary.overallMinWeight ?? summary.minWeight ?? minWeightOption?.weight
  );
  let storefrontStatus =
    availabilityApi && typeof availabilityApi.getStorefrontStatus === 'function'
      ? availabilityApi.getStorefrontStatus(rawQuantity, rawQuantity)
      : rawQuantity > 0
        ? 'AVAILABLE'
        : 'COMING_SOON';

  if (availabilityApi && productId && typeof availabilityApi.getProductAvailability === 'function') {
    const cartAware = availabilityApi.getProductAvailability(productId);
    if (
      cartAware &&
      (cartAware.loading ||
        cartAware.status === 'loading' ||
        cartAware.status === 'idle' ||
        (typeof availabilityApi.isCartStateReady === 'function' && !availabilityApi.isCartStateReady()))
    ) {
      return {
        status: 'loading',
        isLoading: true,
        isLoaded: false,
        hasError: false,
        availableQty: null,
        minWeight: null,
        unit:
          (summary.unit != null && String(summary.unit).trim()) ||
          (minWeightOption?.unit != null && String(minWeightOption.unit).trim()) ||
          'g',
        isWmsSoldOut: false,
        selectedSku,
        selectedSkuQty: null,
        selectedSkuMatched: false,
        canAddToCart: false,
        weightText: META_LOADING_DISPLAY,
        qtyText: META_LOADING_DISPLAY,
        buttonText: (availabilityApi && availabilityApi.LABEL_CHECKING) || 'Checking availability...',
        disabled: true,
        storefrontStatus: '',
      };
    }
    if (cartAware && cartAware.status === 'error') {
      return {
        status: 'error',
        isLoading: false,
        isLoaded: true,
        hasError: true,
        availableQty: null,
        minWeight: null,
        unit:
          (summary.unit != null && String(summary.unit).trim()) ||
          (minWeightOption?.unit != null && String(minWeightOption.unit).trim()) ||
          'g',
        isWmsSoldOut: false,
        selectedSku,
        selectedSkuQty: null,
        selectedSkuMatched: false,
        canAddToCart: false,
        weightText: EMPTY_DISPLAY,
        qtyText: EMPTY_DISPLAY,
        buttonText: 'UNAVAILABLE',
        disabled: true,
        storefrontStatus: '',
      };
    }
    if (cartAware && cartAware.status === 'loaded') {
      const effective = Math.max(0, Math.floor(Number(cartAware.totalAvailableCount) || 0));
      const rawFromApi = Math.max(
        0,
        Math.floor(Number(cartAware.rawWmsTotalAvailableCount ?? rawQuantity) || 0)
      );
      quantity = effective;
      storefrontStatus =
        cartAware.storefrontStatus ||
        (availabilityApi.getStorefrontStatus
          ? availabilityApi.getStorefrontStatus(rawFromApi, effective)
          : effective > 0
            ? 'AVAILABLE'
            : rawFromApi > 0
              ? 'ALL_IN_CART'
              : 'COMING_SOON');
      // Min weight stays API-owned and is independent of remaining qty.
      if (cartAware.minWeight != null) {
        cartAwareMinWeight = normalizeMinWeight(cartAware.minWeight);
      }
    }
  } else if (availabilityApi?.getEffectiveProductInventory && product) {
    const effectiveProduct = availabilityApi.getEffectiveProductInventory(
      {
        productId,
        status: 'loaded',
        totalAvailableCount: rawQuantity,
        rawWmsTotalAvailableCount: rawQuantity,
        skuMap: Object.create(null),
      },
      availabilityApi.getCartSerialSet?.() || new Set()
    );
    if (effectiveProduct) {
      quantity = Math.max(0, Math.floor(Number(effectiveProduct.totalAvailableCount) || 0));
      storefrontStatus = effectiveProduct.storefrontStatus || storefrontStatus;
      cartAwareMinWeight = normalizeMinWeight(effectiveProduct.minWeight);
    }
  }

  const selectedSkuAvailability = product
    ? getSkuAvailabilityFromProduct(product, selectedSku)
    : { matched: false, count: null };
  let selectedSkuCount = selectedSkuAvailability.count;
  const selectedSkuMatched = selectedSkuAvailability.matched;
  if (availabilityApi && productId && selectedSku && typeof availabilityApi.getSkuAvailability === 'function') {
    const skuState = availabilityApi.getSkuAvailability(productId, selectedSku);
    if (skuState && skuState.status === 'loaded') {
      selectedSkuCount = Math.max(0, Math.floor(Number(skuState.count) || 0));
    }
  }

  const allInCartLabel =
    (availabilityApi && availabilityApi.LABEL_ALL_IN_CART) || 'All Available Qty in Cart';
  const unit =
    (summary.unit != null && String(summary.unit).trim()) ||
    (minWeightOption?.unit != null && String(minWeightOption.unit).trim()) ||
    'g';

  if (storefrontStatus === 'AVAILABLE' && quantity > 0) {
    return {
      status: 'available',
      isLoading: false,
      isLoaded: true,
      hasError: false,
      availableQty: quantity,
      minWeight: cartAwareMinWeight,
      unit,
      isWmsSoldOut: false,
      selectedSku,
      selectedSkuQty: selectedSkuCount,
      selectedSkuMatched,
      canAddToCart: true,
      weightText: formatMinWeightDisplay({
        isLoading: false,
        minWeight: cartAwareMinWeight,
        unit,
      }),
      qtyText: String(quantity),
      buttonText: ADD_TO_CART_LABEL,
      disabled: false,
    };
  }

  if (storefrontStatus === 'ALL_IN_CART' || (rawQuantity > 0 && quantity <= 0)) {
    return {
      status: 'all_in_cart',
      isLoading: false,
      isLoaded: true,
      hasError: false,
      availableQty: 0,
      minWeight: cartAwareMinWeight,
      unit,
      isWmsSoldOut: false,
      selectedSku,
      selectedSkuQty: selectedSkuCount,
      selectedSkuMatched,
      // Still open Quick View / Order Sheet so shoppers can edit cart qty/weights.
      canAddToCart: true,
      weightText: formatMinWeightDisplay({
        isLoading: false,
        minWeight: cartAwareMinWeight,
        unit,
      }),
      qtyText: '0',
      buttonText: allInCartLabel,
      disabled: false,
    };
  }

  // Raw WMS quantity === 0, missing product, PRODUCT_NOT_FOUND, empty items, etc.
  // Min weight still shows when the API provided one — independent of qty / Coming Soon.
  return {
    status: 'coming_soon',
    isLoading: false,
    isLoaded: true,
    hasError: false,
    availableQty: 0,
    minWeight: cartAwareMinWeight,
    unit,
    isWmsSoldOut: true,
    selectedSku,
    selectedSkuQty: selectedSkuCount,
    selectedSkuMatched,
    canAddToCart: false,
    weightText: formatMinWeightDisplay({
      isLoading: false,
      minWeight: cartAwareMinWeight,
      unit,
    }),
    qtyText: '0',
    buttonText: COMING_SOON_LABEL,
    disabled: true,
  };
}

/**
 * @param {Record<string, unknown> | null} product
 * @param {{ forceLoading?: boolean, forceError?: boolean, card?: HTMLElement | null }} [options]
 * @returns {ProductInventoryState}
 */
function buildInventoryState(product, options = {}) {
  // forceError is treated as Coming soon — never "Availability unavailable".
  return normalizeWmsAvailability(product, options);
}

/** @param {HTMLElement} action */
function captureActionBaseState(action) {
  if (action.dataset.wmsBaseCaptured === 'true') return;
  action.dataset.wmsBaseCaptured = 'true';
  const labelHost = action.querySelector('.add-to-cart-text__content span span, .quick-add__button-text span span');
  const currentLabel =
    labelHost instanceof HTMLElement ? String(labelHost.textContent || '').trim() : '';
  // Never persist transient WMS loading / coming-soon copy as the restored ATC label.
  const isTransientLabel =
    !currentLabel ||
    /checking availability/i.test(currentLabel) ||
    currentLabel === META_LOADING_DISPLAY ||
    currentLabel === '...' ||
    currentLabel === COMING_SOON_LABEL ||
    /all available qty in cart/i.test(currentLabel) ||
    /availability unavailable/i.test(currentLabel);
  action.dataset.wmsBaseLabel = isTransientLabel ? ADD_TO_CART_LABEL : currentLabel;
  const loadingDisabled = action.dataset.wmsLoadingDisabled === 'true';
  const baseDisabled = action instanceof HTMLButtonElement && action.disabled && !loadingDisabled;
  action.dataset.wmsBaseDisabled = baseDisabled ? 'true' : 'false';
  action.dataset.wmsBaseActive = action.classList.contains('plp-card__atc--active') ? 'true' : 'false';
  action.dataset.wmsBaseDisabledClass = action.classList.contains('plp-card__atc--disabled') ? 'true' : 'false';
}

/**
 * @param {HTMLElement} action
 * @param {string} label
 */
function setActionLabel(action, label) {
  const labelHosts = action.querySelectorAll('.add-to-cart-text__content span span, .quick-add__button-text span span');
  labelHosts.forEach((labelHost) => {
    if (labelHost instanceof HTMLElement) labelHost.textContent = label;
  });
}

/**
 * @param {HTMLElement} action
 * @param {boolean} shouldDisable
 */
function setActionDisabled(action, shouldDisable) {
  if (!(action instanceof HTMLButtonElement)) return;
  action.disabled = shouldDisable;
  action.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
  action.classList.toggle('plp-card__atc--disabled', shouldDisable);
  action.classList.toggle('plp-card__atc--active', !shouldDisable);
}

/**
 * @param {HTMLElement} card
 * @returns {HTMLElement | null}
 */
function findOrCreateGridSoldOutBadge(card) {
  const media = card.querySelector('.plp-card__media');
  if (!(media instanceof HTMLElement)) return null;
  let badge = /** @type {HTMLElement | null} */ (media.querySelector('[data-product-sold-out-badge]'));
  if (!badge) {
    badge = /** @type {HTMLElement | null} */ (media.querySelector('.plp-card__badge--soldout'));
    if (badge) badge.setAttribute('data-product-sold-out-badge', '');
  }
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'plp-card__badge plp-card__badge--soldout';
    badge.setAttribute('data-product-sold-out-badge', '');
    media.prepend(badge);
  }
  return badge;
}

/**
 * @param {HTMLElement} row
 * @returns {HTMLElement | null}
 */
function findOrCreateInventorySoldOutBadge(row) {
  let badge = /** @type {HTMLElement | null} */ (row.querySelector('[data-product-sold-out-badge]'));
  if (!badge) {
    badge = /** @type {HTMLElement | null} */ (
      row.querySelector('.inventory-product-row__stock-badge--oos')
    );
    if (badge) badge.setAttribute('data-product-sold-out-badge', '');
  }
  if (badge) return badge;

  const mediaShell = row.querySelector('.inventory-product-row__media-shell');
  if (mediaShell instanceof HTMLElement) {
    badge = document.createElement('span');
    badge.className = 'inventory-product-row__stock-badge inventory-product-row__stock-badge--oos';
    badge.setAttribute('data-product-sold-out-badge', '');
    mediaShell.prepend(badge);
    return badge;
  }

  const inlineWrap = row.querySelector('.inventory-product-row__badge-inline');
  if (inlineWrap instanceof HTMLElement) {
    badge = document.createElement('span');
    badge.className =
      'inventory-product-row__stock-badge inventory-product-row__stock-badge--oos inventory-product-row__stock-badge--inline';
    badge.setAttribute('data-product-sold-out-badge', '');
    inlineWrap.appendChild(badge);
    return badge;
  }

  return null;
}

/**
 * @param {HTMLElement} card
 * @param {ProductInventoryState} state
 */
function applyInventoryStateToCard(card, state) {
  card.dataset.wmsInventoryStatus = state.status;
  card.dataset.wmsStatus =
    state.status === 'loading'
      ? 'loading'
      : state.status === 'available'
        ? 'available'
        : state.status === 'all_in_cart'
          ? 'all-in-cart'
          : state.status === 'error'
            ? 'error'
            : 'coming-soon';
  card.dataset.wmsLoading = state.isLoading ? 'true' : 'false';
  card.dataset.wmsError = state.hasError || state.status === 'error' ? 'true' : 'false';
  card.dataset.wmsSoldOut = state.isWmsSoldOut ? 'true' : 'false';
  card.dataset.wmsAvailableQty = state.availableQty == null ? '' : String(state.availableQty);
  card.dataset.wmsMinWeight = state.minWeight == null ? '' : String(state.minWeight);

  const shouldShowComingSoon = state.status === 'coming_soon';
  const badgeText = shouldShowComingSoon ? COMING_SOON_LABEL : '';

  if (card.classList.contains('plp-card')) {
    let badge = /** @type {HTMLElement | null} */ (card.querySelector('[data-product-sold-out-badge]'));
    if (shouldShowComingSoon) {
      badge = badge || findOrCreateGridSoldOutBadge(card);
      if (badge) {
        badge.hidden = false;
        badge.textContent = badgeText;
        badge.classList.add('plp-card__badge--soldout');
      }
    } else if (badge) {
      badge.hidden = true;
    }
  } else if (card.classList.contains('inventory-product-row')) {
    let badge = /** @type {HTMLElement | null} */ (card.querySelector('[data-product-sold-out-badge]'));
    if (shouldShowComingSoon) {
      badge = badge || findOrCreateInventorySoldOutBadge(card);
      if (badge) {
        badge.hidden = false;
        badge.textContent = badgeText;
        badge.classList.add('inventory-product-row__stock-badge--oos');
        const rowClassHost = card.closest('.inventory-product-row') || card;
        if (rowClassHost instanceof HTMLElement) {
          rowClassHost.classList.remove('inventory-product-row--stock-ready', 'inventory-product-row--stock-low');
          rowClassHost.classList.add('inventory-product-row--stock-oos');
        }
      }
    } else if (badge) {
      badge.hidden = true;
    }

    // View Details stays clickable for Charm inline accordion even when WMS qty is 0.
    // Do not force-close an open accordion on every WMS paint — that fights toggle state.
    card.querySelectorAll('[data-order-sheet-details-toggle], [data-plp-details-toggle]').forEach((toggleEl) => {
      if (!(toggleEl instanceof HTMLButtonElement)) return;
      if (toggleEl.dataset.wmsBaseDisabled == null) {
        toggleEl.dataset.wmsBaseDisabled = toggleEl.disabled ? 'true' : 'false';
      }
      const baseDisabled = toggleEl.dataset.wmsBaseDisabled === 'true';
      toggleEl.disabled = baseDisabled;
      toggleEl.setAttribute('aria-disabled', baseDisabled ? 'true' : 'false');
    });
  }

  const actionNodes = card.querySelectorAll('[data-product-action], .plp-card__atc');
  const availabilityApi = getWmsAvailabilityApi();
  const allInCartLabel =
    (availabilityApi && availabilityApi.LABEL_ALL_IN_CART) || 'All Available Qty in Cart';
  actionNodes.forEach((actionNode) => {
    if (!(actionNode instanceof HTMLElement)) return;
    captureActionBaseState(actionNode);
    const baseLabel = String(actionNode.dataset.wmsBaseLabel || '').trim() || ADD_TO_CART_LABEL;
    const buttonLabel =
      state.status === 'loading'
        ? BUTTON_LOADING_DISPLAY
        : state.status === 'coming_soon'
          ? COMING_SOON_LABEL
          : state.status === 'all_in_cart'
            ? allInCartLabel
            : state.status === 'error'
              ? 'UNAVAILABLE'
              : baseLabel || state.buttonText || ADD_TO_CART_LABEL;
    const shouldDisable =
      state.status === 'loading' ||
      state.status === 'coming_soon' ||
      state.status === 'error' ||
      !state.canAddToCart;
    actionNode.setAttribute('aria-busy', state.status === 'loading' ? 'true' : 'false');

    if (state.status === 'coming_soon') {
      if (availabilityApi?.applyButtonState) {
        availabilityApi.applyButtonState(actionNode, 'coming-soon', {
          label: COMING_SOON_LABEL,
          disabled: true,
        });
      } else {
        setActionLabel(actionNode, COMING_SOON_LABEL);
        setActionDisabled(actionNode, true);
      }
      actionNode.setAttribute('data-wms-state', 'coming-soon');
      return;
    }

    if (state.status === 'all_in_cart') {
      if (availabilityApi?.applyButtonState) {
        availabilityApi.applyButtonState(actionNode, 'all-in-cart', {
          label: allInCartLabel,
          disabled: false,
        });
      } else {
        setActionLabel(actionNode, allInCartLabel);
        setActionDisabled(actionNode, false);
      }
      actionNode.setAttribute('data-wms-state', 'all-in-cart');
      return;
    }

    if (state.status === 'loading') {
      if (availabilityApi?.applyButtonState) {
        availabilityApi.applyButtonState(actionNode, 'loading', {
          label: BUTTON_LOADING_DISPLAY,
          disabled: true,
        });
      } else {
        setActionLabel(actionNode, BUTTON_LOADING_DISPLAY);
        setActionDisabled(actionNode, true);
      }
      actionNode.setAttribute('data-wms-state', 'loading');
      return;
    }

    if (availabilityApi?.applyButtonState) {
      availabilityApi.applyButtonState(actionNode, 'available', {
        label: buttonLabel,
        disabled: shouldDisable,
      });
    } else {
      setActionLabel(actionNode, buttonLabel);
      setActionDisabled(actionNode, shouldDisable);
    }
    actionNode.setAttribute('data-wms-state', 'available');
  });
}

/** @returns {Element[]} */
function getGridRoots() {
  /** @type {Element[]} */
  const roots = [];
  for (const selector of GRID_ROOT_SELECTORS) {
    document.querySelectorAll(selector).forEach((el) => {
      if (!roots.includes(el)) roots.push(el);
    });
  }
  return roots;
}

/**
 * Collect product cards that expose a Shopify product ID.
 * @param {ParentNode} root
 * @returns {HTMLElement[]}
 */
function collectWeightCards(root) {
  /** @type {HTMLElement[]} */
  const cards = [];
  const seen = new Set();

  root.querySelectorAll(CARD_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.closest(IGNORE_SELECTOR)) return;
    const inGrid = GRID_ROOT_SELECTORS.some((sel) => el.closest(sel));
    if (!inGrid && root !== document) {
      // allow scoped roots that are themselves cards
    } else if (!inGrid && root === document) {
      return;
    }

    const productId = normalizeShopifyId(el.dataset.productId);
    if (!productId) return;

    // Prefer the card that owns the weight display nodes
    const displayHost =
      el.querySelector('[data-min-weight-value], [data-available-qty-value]') != null
        ? el
        : el.closest('.plp-card, .inventory-product-row') || el;

    if (!(displayHost instanceof HTMLElement)) return;
    if (seen.has(displayHost)) return;
    seen.add(displayHost);
    cards.push(displayHost);
  });

  return cards;
}

/** @returns {string[]} */
function collectRenderedProductIds(scope = document) {
  return [
    ...new Set(
      collectWeightCards(scope)
        .map((card) => normalizeShopifyId(card.dataset.productId))
        .filter(Boolean)
    ),
  ];
}

/**
 * @param {number} grams
 * @returns {string}
 */
function formatCartWeightDisplay(grams) {
  const value = Number(grams);
  if (!Number.isFinite(value) || value <= 0) return '0.000 g';
  return `${(Math.round((value + Number.EPSILON) * 1000) / 1000).toFixed(3)} g`;
}

/**
 * Resolve product cart qty/weight from the shared WMS cart cache (no extra /cart.js calls).
 * @param {HTMLElement} card
 * @param {Record<string, { quantity: number, totalWeight: number }>|null} [summaryByProduct]
 * @returns {{ quantity: number, totalWeight: number }}
 */
function resolveCardCartSummary(card, summaryByProduct) {
  const productId = normalizeShopifyId(card.dataset.productId);
  if (!productId) return { quantity: 0, totalWeight: 0 };

  if (summaryByProduct) {
    const entry = summaryByProduct[productId];
    return {
      quantity: Math.max(0, Math.floor(Number(entry?.quantity) || 0)),
      totalWeight: Math.max(0, Number(entry?.totalWeight) || 0),
    };
  }

  const availabilityApi = getWmsAvailabilityApi();
  if (availabilityApi && typeof availabilityApi.getProductCartSummary === 'function') {
    const summary = availabilityApi.getProductCartSummary(productId);
    return {
      quantity: Math.max(0, Math.floor(Number(summary?.quantity) || 0)),
      totalWeight: Math.max(0, Number(summary?.totalWeight) || 0),
    };
  }

  return { quantity: 0, totalWeight: 0 };
}

/**
 * Paint Order Sheet + Grid "In Cart Qty" / "Cart Weight" from shared cart summary.
 * No-ops only when the card does not render these nodes.
 * @param {HTMLElement} card
 * @param {Record<string, { quantity: number, totalWeight: number }>|null} [summaryByProduct]
 */
function setCardCartSummaryDisplay(card, summaryByProduct) {
  const qtyEl = card.querySelector('[data-in-cart-qty-value]');
  const weightEl = card.querySelector('[data-cart-weight-value]');
  if (!(qtyEl instanceof HTMLElement) && !(weightEl instanceof HTMLElement)) return;

  const summary = resolveCardCartSummary(card, summaryByProduct);
  const qtyText = String(summary.quantity);
  const weightText = formatCartWeightDisplay(summary.totalWeight);

  if (qtyEl instanceof HTMLElement && qtyEl.textContent !== qtyText) {
    qtyEl.textContent = qtyText;
  }
  if (weightEl instanceof HTMLElement && weightEl.textContent !== weightText) {
    weightEl.textContent = weightText;
  }
}

/**
 * Shopify available qty already summed across the merged family in Liquid.
 * Absent on normal products so their existing WMS qty path stays unchanged.
 * @param {HTMLElement} card
 * @returns {number | null}
 */
function readMergedShopifyAvailableQty(card) {
  if (!(card instanceof HTMLElement)) return null;
  if (!card.hasAttribute('data-shopify-merged-available-qty')) return null;
  const parsed = Number(card.getAttribute('data-shopify-merged-available-qty'));
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.floor(parsed));
}

/**
 * @param {HTMLElement} card
 * @param {ProductInventoryState} inventoryState
 * @param {Record<string, { quantity: number, totalWeight: number }>|null} [summaryByProduct]
 * @param {{ updateCartSummary?: boolean }} [options]
 */
function setCardDisplay(card, inventoryState, summaryByProduct = null, options = {}) {
  isApplyingDom = true;
  try {
    const state =
      inventoryState.status === 'coming_soon' ||
      inventoryState.status === 'available' ||
      inventoryState.status === 'all_in_cart'
        ? 'ready'
        : inventoryState.status === 'loading'
          ? 'loading'
          : 'ready';
    card.dataset.minWeightState = state;
    if (state === 'ready') {
      card.dataset.weightHydrated = 'true';
    } else {
      delete card.dataset.weightHydrated;
    }

    const weightText =
      inventoryState.weightText != null
        ? inventoryState.weightText
        : state === 'loading'
          ? META_LOADING_DISPLAY
          : EMPTY_DISPLAY;
    const mergedShopifyQty = readMergedShopifyAvailableQty(card);
    const qtyText =
      mergedShopifyQty != null
        ? String(mergedShopifyQty)
        : inventoryState.qtyText != null
          ? inventoryState.qtyText
          : state === 'loading'
            ? META_LOADING_DISPLAY
            : '0';

    card.querySelectorAll('[data-min-weight-value]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.textContent !== weightText) el.textContent = weightText;
      if (state === 'loading') {
        el.setAttribute('data-min-weight-loading', '');
        el.setAttribute('data-state', 'loading');
      } else {
        el.removeAttribute('data-min-weight-loading');
        el.setAttribute('data-state', 'ready');
      }
    });

    const qtyState = mergedShopifyQty != null ? 'ready' : state === 'loading' ? 'loading' : 'ready';
    card.querySelectorAll('[data-available-qty-value]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.textContent !== qtyText) el.textContent = qtyText;
      el.setAttribute('data-state', qtyState);
    });

    // Keep Wizzy/PLP qty-row loading flag in sync. Leaving data-qty-state="loading"
    // after paint keeps the Available Qty value at opacity 0.55 (looks non-bold).
    card
      .querySelectorAll(
        '[data-wizzy-available-qty], .plp-card__meta-item--qty, .inventory-product-row__meta-item--qty'
      )
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.setAttribute('data-qty-state', qtyState);
      });

    const row = card.querySelector('.plp-card__min-weight');
    if (row instanceof HTMLElement) {
      row.setAttribute('data-min-weight-state', card.dataset.minWeightState || state);
    }

    applyInventoryStateToCard(card, inventoryState);
    if (options.updateCartSummary !== false) {
      setCardCartSummaryDisplay(card, summaryByProduct);
    }
  } finally {
    queueMicrotask(() => {
      isApplyingDom = false;
    });
  }
}

/** @param {string[]} productIds */
function setCurrentCardsLoading(productIds) {
  const idSet = new Set(productIds.map(normalizeShopifyId).filter(Boolean));
  const availabilityApi = getWmsAvailabilityApi();
  availabilityApi?.markProductsLoading?.([...idSet]);
  collectWeightCards(document).forEach((card) => {
    const id = normalizeShopifyId(card.dataset.productId);
    if (!idSet.has(id)) return;
    setCardDisplay(card, buildInventoryState(null, { forceLoading: true, card }));
  });
}

/**
 * Timeout / network failure → Coming soon for every unresolved product (never Availability unavailable).
 * @param {string[]} productIds
 */
function setCurrentCardsComingSoon(productIds) {
  const idSet = new Set(productIds.map(normalizeShopifyId).filter(Boolean));
  const availabilityApi = getWmsAvailabilityApi();
  availabilityApi?.markProductsError?.([...idSet], 'PRODUCT_BATCH_UNAVAILABLE');
  collectWeightCards(document).forEach((card) => {
    const id = normalizeShopifyId(card.dataset.productId);
    if (!idSet.has(id)) return;
    setCardDisplay(card, buildInventoryState(null, { forceError: true, card }));
  });
}

/** @deprecated Use setCurrentCardsComingSoon — kept for any external callers. */
function setCurrentCardsUnavailable(productIds) {
  setCurrentCardsComingSoon(productIds);
}

/**
 * @param {unknown[]} products
 */
function cacheProductWeightData(products) {
  const store = ensureStore();
  const shared = ensureSharedWeightStore();
  const availabilityApi = getWmsAvailabilityApi();
  if (!Array.isArray(products)) return;

  products.forEach((product) => {
    if (!product || typeof product !== 'object') return;
    const productObj = /** @type {Record<string, unknown>} */ (product);
    const productId = normalizeShopifyId(productObj.productId);
    if (!productId) return;

    store.products.set(productId, productObj);

    const skuItems = Array.isArray(productObj.skus)
      ? productObj.skus
      : Array.isArray(productObj.items)
        ? productObj.items
        : [];
    skuItems.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const itemObj = /** @type {Record<string, unknown>} */ (item);
      const sku = String(itemObj.sku || '').trim();
      if (sku) {
        const entry = { productId, item: itemObj };
        store.skus.set(sku, entry);
        const normalizedSku = sku.toUpperCase();
        if (normalizedSku !== sku) store.skus.set(normalizedSku, entry);
        // Keep legacy shared store in sync so QV / weight modal need no API call
        shared.rawBySku[sku.toLowerCase()] = itemObj;
        delete shared.dataBySku[sku.toLowerCase()];
      }

      const variants = Array.isArray(itemObj.variants) ? itemObj.variants : [];
      variants.forEach((variant) => {
        if (!variant || typeof variant !== 'object') return;
        const variantId = normalizeShopifyId(
          /** @type {Record<string, unknown>} */ (variant).variantId
        );
        if (!variantId) return;
        store.variants.set(variantId, { productId, sku, item: itemObj });
        if (availabilityApi?.registerVariant && sku) {
          availabilityApi.registerVariant(variantId, productId, sku);
        }
      });
    });
  });
}

/**
 * @param {unknown[]} products
 * @param {string[]} [requestedIds]
 */
function updateRenderedProductCards(products, requestedIds) {
  if (!Array.isArray(products)) return;

  /** @type {Map<string, Record<string, unknown>>} */
  const productsById = new Map();
  products.forEach((product) => {
    if (!product || typeof product !== 'object') return;
    const obj = /** @type {Record<string, unknown>} */ (product);
    const id = normalizeShopifyId(obj.productId);
    if (id) productsById.set(String(id), obj);
  });

  const scopeIds = new Set(
    (requestedIds || [...productsById.keys()]).map((id) => String(normalizeShopifyId(id))).filter(Boolean)
  );

  collectWeightCards(document).forEach((card) => {
    const productId = String(normalizeShopifyId(card.dataset.productId));
    if (!productId || !scopeIds.has(productId)) return;

    const product = productsById.get(productId);
    if (!product) {
      console.warn('[WMS availability] Product missing from weight response:', productId);
      setCardDisplay(card, normalizeWmsAvailability(null, { card }));
      return;
    }

    const normalizedState = normalizeWmsAvailability(product, { card });
    setCardDisplay(card, normalizedState);
  });
}

/**
 * @param {string[]} productIds
 * @param {AbortSignal} [signal]
 * @returns {Promise<unknown[]>}
 */
async function fetchProductWeights(productIds, signal) {
  const normalizedIds = [...new Set(productIds.map(normalizeShopifyId).filter(Boolean))];
  if (!normalizedIds.length) return [];

  const url = getProductsBatchUrl();
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: requestHeaders(url),
    body: JSON.stringify({ productIds: normalizedIds }),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `[A-One WMS] Product weights request failed with status ${response.status}.`
    );
  }

  let payload;
  try {
    payload = await response.json();
  } catch {
    throw new Error('[A-One WMS] Product weights API returned invalid JSON.');
  }

  if (!payload?.success || !Array.isArray(payload?.data?.products)) {
    throw new Error('[A-One WMS] Product weights API returned an invalid response.');
  }

  return payload.data.products;
}

/**
 * @param {string[]} ids
 * @returns {boolean}
 */
function allProductsCached(ids) {
  const store = ensureStore();
  return ids.every((id) => store.products.has(id));
}

/**
 * Apply already-cached product entries to the current cards.
 * @param {string[]} ids
 */
function applyCachedProductsToCards(ids) {
  const store = ensureStore();
  const products = ids
    .map((id) => store.products.get(id))
    .filter(Boolean);
  updateRenderedProductCards(products, ids);
}

/**
 * Start one products-batch HTTP call for the given parent product ID(s).
 * Callers should pass a single parent id; the queue enforces that. Multi-id
 * remains supported for API compatibility but is not used by Collection/SLP hydrate.
 * Page-level activeRequest* is owned by loadCurrentProductWeights (not here) so
 * concurrent per-parent POSTs cannot clobber each other.
 *
 * @param {string[]} ids
 * @param {{ source?: string }} [meta]
 * @returns {Promise<unknown[]>}
 */
function startProductsBatchRequest(ids, meta = {}) {
  const store = ensureStore();
  const requestKey = createProductRequestKey(ids);
  const generation = ++requestGeneration;
  const cfg = getWmsConfiguration();
  const source = meta.source || 'products-batch';

  /** @type {(value: unknown[]) => void} */
  let settle = () => {};
  /** @type {Promise<unknown[]>} */
  const request = new Promise((resolve) => {
    settle = resolve;
  });

  ids.forEach((id) => {
    inFlightByProductId.set(id, request);
  });

  store.abortController = null;
  store.status = 'loading';
  store.lastError = null;
  store.currentRenderedProductIds = new Set([
    ...(store.currentRenderedProductIds || []),
    ...ids,
  ]);

  (async () => {
    /** @type {ReturnType<typeof setTimeout> | null} */
    let timeoutId = null;
    /** @type {AbortController | null} */
    let timeoutController = null;

    try {
      setCurrentCardsLoading(ids);
      debugLog('API Request', { productIds: ids, generation, key: requestKey, source });

      timeoutController = new AbortController();
      timeoutId = window.setTimeout(() => {
        if (timeoutController) timeoutController.abort();
      }, cfg.timeoutMs);

      const products = await fetchProductWeights(ids, timeoutController.signal);

      // Always cache — useful for ATC even if a newer page request superseded us.
      cacheProductWeightData(products);
      getWmsAvailabilityApi()?.upsertBatchProducts?.(products, ids);

      // Wait for shared cart serials before painting so cards never flash raw WMS qty.
      try {
        await getWmsAvailabilityApi()?.ensureCartSerialsSynced?.(false);
      } catch (_error) {
        /* paint with best-known cart state */
      }

      // Always paint the IDs this request owns. Product-id keyed updates are safe even when
      // another overlapping request is in flight for different IDs (avoids stuck "Checking...").
      // Race guard: updateRenderedProductCards only paints cards whose current data-product-id
      // still matches the requested parent id.
      updateRenderedProductCards(products, ids);
      store.status = 'success';
      debugLog('API Response', { count: products.length, generation, source });
      settle(products);
    } catch (error) {
      if (error && /** @type {Error} */ (error).name === 'AbortError') {
        console.error('[WMS availability] Request timed out', { generation, key: requestKey, source });
        store.status = 'error';
        store.lastError = error;
        setCurrentCardsComingSoon(ids);
        settle([]);
        return;
      }

      console.error('[WMS availability] Request failed', error);
      store.status = 'error';
      store.lastError = error;
      setCurrentCardsComingSoon(ids);
      getWmsAvailabilityApi()?.markProductsError?.(ids, 'PRODUCT_BATCH_FAILED');
      settle([]);
    } finally {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      ids.forEach((id) => {
        if (inFlightByProductId.get(id) === request) {
          inFlightByProductId.delete(id);
        }
      });
    }
  })();

  return request;
}

/**
 * Drain the per-parent queue up to MAX_CONCURRENT_PRODUCT_REQUESTS.
 * Each slot runs startProductsBatchRequest([id]) — same endpoint/contract as before.
 */
function pumpProductWeightQueue() {
  while (
    activeProductRequestCount < MAX_CONCURRENT_PRODUCT_REQUESTS &&
    pendingProductRequestQueue.length
  ) {
    const item = pendingProductRequestQueue.shift();
    if (!item) break;

    const { id, source, resolveOuter } = item;
    activeProductRequestCount += 1;

    const request = startProductsBatchRequest([id], { source });
    // Keep dedupe map pointed at the live HTTP promise while this parent is fetching.
    inFlightByProductId.set(id, request);

    request
      .then((products) => {
        resolveOuter(Array.isArray(products) ? products : []);
      })
      .catch(() => {
        // startProductsBatchRequest already settles failures as []; keep outer safe.
        resolveOuter([]);
      })
      .finally(() => {
        activeProductRequestCount = Math.max(0, activeProductRequestCount - 1);
        pumpProductWeightQueue();
      });
  }
}

/**
 * Schedule one parent-product products-batch. Registers inFlight immediately so
 * duplicate callers (MutationObserver / Wizzy / ensureProductsLoaded) join the same promise.
 * @param {string} productId
 * @param {string} source
 * @returns {Promise<unknown[]>}
 */
function scheduleProductWeightRequest(productId, source) {
  const id = normalizeShopifyId(productId);
  if (!id) return Promise.resolve([]);

  const existing = inFlightByProductId.get(id);
  if (existing) return existing;

  /** @type {(value: unknown[]) => void} */
  let resolveOuter = () => {};
  /** @type {Promise<unknown[]>} */
  const outer = new Promise((resolve) => {
    resolveOuter = resolve;
  });

  inFlightByProductId.set(id, outer);
  pendingProductRequestQueue.push({ id, source, resolveOuter });
  pumpProductWeightQueue();
  return outer;
}

/**
 * Enqueue one products-batch POST per parent product ID, with controlled concurrency.
 * Does not merge IDs into a single large request (avoids 504s on big variant trees).
 * @param {string[]} ids
 * @param {{ source?: string }} [meta]
 * @returns {Promise<unknown[]>}
 */
function enqueueProductsBatchRequest(ids, meta = {}) {
  const uniqueIds = [
    ...new Set((ids || []).map(normalizeShopifyId).filter(Boolean)),
  ];
  if (!uniqueIds.length) return Promise.resolve([]);

  const source = meta.source || 'products-batch';

  const promises = uniqueIds.map((id) => {
    const inFlight = inFlightByProductId.get(id);
    if (inFlight) return inFlight;
    return scheduleProductWeightRequest(id, source);
  });

  // Independent parents: one 504 must not abort siblings.
  return Promise.allSettled(promises).then((results) => {
    /** @type {unknown[]} */
    const products = [];
    results.forEach((result) => {
      if (result.status !== 'fulfilled' || !Array.isArray(result.value)) return;
      products.push(...result.value);
    });
    return products;
  });
}

/**
 * Shared products-batch entry point for collection / Wizzy / QV / WMS availability.
 * Fetches one parent product per POST (controlled concurrency); never merges the
 * full result set into a single products-batch body.
 *
 * @param {string[]} [productIds]
 * @param {{
 *   source?: string,
 *   preferCache?: boolean,
 * }} [options]
 *   preferCache — reuse store.products entries for IDs already fetched (collection hydrate,
 *   WMS ensure, and PDP ensureWmsProductData). Omit only when a caller must force a refetch.
 *   Concurrent overlapping requests always reuse inFlightByProductId (never a permanent cache).
 * @returns {Promise<unknown[]>}
 */
function loadCurrentProductWeights(productIds, options = {}) {
  const store = ensureStore();
  const source = options.source || 'api';
  const preferCache = options.preferCache === true;
  const ids = [
    ...new Set(
      (productIds && productIds.length ? productIds : collectRenderedProductIds())
        .map(normalizeShopifyId)
        .filter(Boolean)
    ),
  ];
  const requestKey = createProductRequestKey(ids);

  if (!ids.length) return Promise.resolve([]);

  // Same exact result set already in flight — reuse (no second network call).
  if (activeRequestPromise && activeRequestKey === requestKey) {
    debugLog('Reusing in-flight products-batch', { key: requestKey, source });
    return activeRequestPromise;
  }
  if (store.activeRequest && store.activeRequestKey === requestKey) {
    debugLog('Reusing in-flight products-batch (store)', { key: requestKey, source });
    return store.activeRequest;
  }

  // Same membership already applied and fully cached — paint from memory, no network.
  if (requestKey === lastAppliedRequestKey && allProductsCached(ids)) {
    debugLog('Skip network — cache hit for current result set', { key: requestKey, source });
    applyCachedProductsToCards(ids);
    return Promise.resolve(ids.map((id) => store.products.get(id)).filter(Boolean));
  }

  const alreadyInFlight = ids.filter((id) => inFlightByProductId.has(id));
  const cachedReady = ids.filter(
    (id) => !inFlightByProductId.has(id) && preferCache && store.products.has(id)
  );
  const newIdsToFetch = ids.filter((id) => {
    if (inFlightByProductId.has(id)) return false;
    if (preferCache && store.products.has(id)) return false;
    return true;
  });

  if (getWmsConfiguration().debug) {
    console.debug('[Inventory Batch]', {
      source,
      requestedProductIds: ids,
      uniqueProductIds: ids,
      alreadyInFlight,
      cachedReady,
      newIdsToFetch,
      mode: 'per-parent',
      concurrency: MAX_CONCURRENT_PRODUCT_REQUESTS,
    });
  }

  /** @type {Promise<unknown[]>[]} */
  const parts = [];

  if (alreadyInFlight.length) {
    const uniqueInFlight = [
      ...new Set(
        alreadyInFlight
          .map((id) => inFlightByProductId.get(id))
          .filter(Boolean)
      ),
    ];
    parts.push(
      Promise.allSettled(uniqueInFlight).then((results) => {
        /** @type {unknown[]} */
        const products = [];
        results.forEach((result) => {
          if (result.status !== 'fulfilled' || !Array.isArray(result.value)) return;
          products.push(...result.value);
        });
        return products;
      })
    );
  }

  if (newIdsToFetch.length) {
    parts.push(enqueueProductsBatchRequest(newIdsToFetch, { source }));
  }

  if (!parts.length) {
    // Everything already cached (preferCache) or nothing to do.
    applyCachedProductsToCards(ids);
    if (allProductsCached(ids)) {
      lastAppliedRequestKey = requestKey;
      lastSeenMembershipKey = requestKey;
    }
    return Promise.resolve(ids.map((id) => store.products.get(id)).filter(Boolean));
  }

  // allSettled: one parent failure must not reject the whole page hydrate.
  const combined = Promise.allSettled(parts).then(() => {
    if (allProductsCached(ids)) {
      lastAppliedRequestKey = requestKey;
      lastSeenMembershipKey = requestKey;
      applyCachedProductsToCards(ids);
    }
    return ids.map((id) => store.products.get(id)).filter(Boolean);
  });

  // Always expose combined as the page-level waiter. The concurrency queue may delay
  // individual POSTs, so do not rely on startProductsBatchRequest having run synchronously.
  activeRequestKey = requestKey;
  activeRequestPromise = combined;
  store.activeRequestKey = requestKey;
  store.activeRequest = combined;

  return combined.finally(() => {
    if (activeRequestPromise === combined) {
      activeRequestPromise = null;
      activeRequestKey = '';
    }
    if (store.activeRequest === combined) {
      store.activeRequest = null;
      store.activeRequestKey = null;
    }
  });
}

async function waitForCurrentWeightData() {
  const store = ensureStore();
  /** @type {Promise<unknown>[]} */
  const waiters = [];
  if (store.activeRequest) waiters.push(store.activeRequest);
  else if (activeRequestPromise) waiters.push(activeRequestPromise);
  if (inFlightByProductId.size) {
    waiters.push(...new Set(inFlightByProductId.values()));
  }
  if (waiters.length) await Promise.allSettled(waiters);
}

/**
 * @param {ParentNode} [scope]
 */
async function hydrate(scope = document) {
  const cfg = getWmsConfiguration();
  if (!cfg.enabled) return;

  if (!cfg.baseUrl) {
    console.error(
      '[WMS availability] API base URL is missing. Configure it in Shopify Theme settings → WMS API Integration.'
    );
    collectWeightCards(scope).forEach((card) => {
      setCardDisplay(card, normalizeWmsAvailability(null, { card }));
    });
    return;
  }

  const productIds = collectRenderedProductIds(scope);
  if (!productIds.length) {
    debugLog('No product IDs found in scope');
    return;
  }

  const membershipKey = createProductRequestKey(productIds);

  // No-op if membership unchanged and either in-flight or already applied.
  // Still re-paint from cache when DOM nodes were replaced but product IDs did not change.
  if (
    membershipKey === lastSeenMembershipKey &&
    (activeRequestKey === membershipKey || lastAppliedRequestKey === membershipKey)
  ) {
    if (lastAppliedRequestKey === membershipKey && allProductsCached(productIds)) {
      const stillNeedsPaint = collectWeightCards(scope).some((card) => {
        return card.dataset.minWeightState === 'loading' || !card.dataset.weightHydrated;
      });
      if (stillNeedsPaint) {
        applyCachedProductsToCards(productIds);
      }
    }
    debugLog('Skip hydrate — membership unchanged', { key: membershipKey });
    return;
  }

  lastSeenMembershipKey = membershipKey;
  debugLog('Collected product IDs', productIds);
  await loadCurrentProductWeights(productIds, {
    source: pendingHydrateSource || 'collection-init',
    preferCache: true,
  });
}

/** @param {ParentNode} [scope] */
/**
 * @param {ParentNode} [scope]
 * @param {{ source?: string }} [meta]
 */
function scheduleHydrate(scope = document, meta = {}) {
  const cfg = getWmsConfiguration();
  if (!cfg.enabled) return;

  pendingHydrateScope = scope;
  if (meta.source) pendingHydrateSource = meta.source;
  const now = Date.now();
  if (!debounceStartedAt) debounceStartedAt = now;

  // Only one intended debounce timer may flush — clear any previous.
  if (debounceTimer) window.clearTimeout(debounceTimer);

  const elapsed = now - debounceStartedAt;
  const wait = elapsed >= MAX_DEBOUNCE_WAIT_MS ? 0 : DEBOUNCE_MS;

  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    debounceStartedAt = 0;
    const target = pendingHydrateScope || document;
    hydrate(target);
  }, wait);
}

/**
 * True when product-card membership may have changed (ignore our own weight text updates).
 * @param {MutationRecord[]} mutations
 */
function mutationNeedsHydrate(mutations) {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-id') {
      const el = mutation.target;
      if (el instanceof HTMLElement && normalizeShopifyId(el.dataset.productId)) {
        if (el.dataset.weightHydrated !== 'true') return true;
      }
      continue;
    }

    if (mutation.type !== 'childList') continue;

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (
        node.matches?.(
          '[data-product-id], .plp-card, .plp-product-grid__item, .inventory-product-row'
        )
      ) {
        return true;
      }
      if (
        node.querySelector?.(
          '[data-product-id], .plp-card, .plp-product-grid__item, .inventory-product-row'
        )
      ) {
        return true;
      }
    }

    // Removals (pagination replacing the grid) also change membership.
    for (const node of mutation.removedNodes) {
      if (!(node instanceof Element)) continue;
      if (
        node.matches?.(
          '[data-product-id], .plp-card, .plp-product-grid__item, .inventory-product-row'
        ) ||
        node.querySelector?.(
          '[data-product-id], .plp-card, .plp-product-grid__item, .inventory-product-row'
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

function observeGrids() {
  if (gridObserver) gridObserver.disconnect();

  gridObserver = new MutationObserver((mutations) => {
    if (isApplyingDom) return;
    if (!mutationNeedsHydrate(mutations)) return;

    // Cheap fingerprint check before scheduling another hydrate.
    const nextIds = collectRenderedProductIds(document);
    const nextKey = createProductRequestKey(nextIds);
    if (
      nextKey &&
      nextKey === lastSeenMembershipKey &&
      (activeRequestKey === nextKey || lastAppliedRequestKey === nextKey)
    ) {
      // Same product IDs, but grid DOM may have been replaced (filters / Wizzy / pagination).
      // Re-apply cached WMS state so new nodes do not stay stuck on "Checking availability...".
      if (lastAppliedRequestKey === nextKey && allProductsCached(nextIds)) {
        const stillNeedsPaint = collectWeightCards(document).some((card) => {
          return card.dataset.minWeightState === 'loading' || !card.dataset.weightHydrated;
        });
        if (stillNeedsPaint) {
          debugLog('Re-paint replaced cards from WMS cache', { key: nextKey });
          applyCachedProductsToCards(nextIds);
        }
      }
      return;
    }

    debugLog('Pagination / DOM update triggered');
    scheduleHydrate(document, { source: 'mutation-observer' });
  });

  const roots = getGridRoots();
  const observeOpts = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-id'],
  };

  if (!roots.length) {
    const main = document.getElementById('MainContent') || document.body;
    gridObserver.observe(main, observeOpts);
    return;
  }

  for (const root of roots) {
    gridObserver.observe(root, observeOpts);
  }
}

function bindThemeEvents() {
  document.addEventListener('filter:update', () => {
    debugLog('FilterUpdate triggered');
    // Clear membership fingerprints so the next hydrate may network for the new set.
    // Do not clear inFlightByProductId — overlapping IDs still reuse in-flight promises.
    lastSeenMembershipKey = '';
    lastAppliedRequestKey = '';
    window.setTimeout(() => {
      observeGrids();
      scheduleHydrate(document, { source: 'filter-update' });
    }, 80);
  });

  document.addEventListener('shopify:section:load', () => {
    lastSeenMembershipKey = '';
    lastAppliedRequestKey = '';
    observeGrids();
    scheduleHydrate(document, { source: 'section-load' });
  });

  window.addEventListener('popstate', () => {
    debugLog('popstate triggered');
    lastSeenMembershipKey = '';
    lastAppliedRequestKey = '';
    window.setTimeout(() => {
      observeGrids();
      scheduleHydrate(document, { source: 'popstate' });
    }, 80);
  });

  window.addEventListener('pageshow', (event) => {
    const navEntries =
      typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')
        : [];
    const navType = navEntries && navEntries[0] ? navEntries[0].type : '';
    const restoredFromCache = Boolean(event && event.persisted) || navType === 'back_forward';
    if (!restoredFromCache) return;

    debugLog('pageshow triggered (BFCache / back_forward)');
    lastSeenMembershipKey = '';
    lastAppliedRequestKey = '';
    window.setTimeout(() => {
      observeGrids();
      scheduleHydrate(document, { source: 'pageshow' });
    }, 80);
  });
}

/**
 * Re-paint hydrated cards from cached WMS data using the latest cart serial set.
 * Does not re-fetch WMS — only reapplies cart-aware filtering.
 * Always repaints all cached cards: removals would be missed if scoped only to current cart lines.
 * @param {{ mutationGeneration?: number, loading?: boolean, cartReady?: boolean, cart?: any }|null} [detail]
 */
function reapplyCartAwareCardDisplays(detail) {
  const store = ensureStore();
  if (!store.products || typeof store.products.forEach !== 'function') return;

  const availabilityApi = getWmsAvailabilityApi();
  /** @type {Record<string, { quantity: number, totalWeight: number }>|null} */
  let summaryByProduct = null;
  if (availabilityApi && typeof availabilityApi.buildCartSummaryByProduct === 'function') {
    summaryByProduct = availabilityApi.buildCartSummaryByProduct(detail && detail.cart);
  }

  if (detail && (detail.loading === true || detail.cartReady === false)) {
    collectWeightCards(document).forEach((card) => {
      const productId = String(normalizeShopifyId(card.dataset.productId) || '');
      if (!productId || !store.products.has(productId)) return;
      // Preserve last-known In Cart Qty / Cart Weight while cart refreshes.
      setCardDisplay(card, buildInventoryState(null, { forceLoading: true, card }), null, {
        updateCartSummary: false,
      });
    });
    return;
  }

  isApplyingDom = true;
  try {
    collectWeightCards(document).forEach((card) => {
      const productId = String(normalizeShopifyId(card.dataset.productId) || '');
      if (!productId || !store.products.has(productId)) return;
      const product = store.products.get(productId);
      if (!product || typeof product !== 'object') return;
      setCardDisplay(
        card,
        normalizeWmsAvailability(/** @type {Record<string, unknown>} */ (product), { card }),
        summaryByProduct
      );
    });
  } finally {
    queueMicrotask(() => {
      isApplyingDom = false;
    });
  }
}

/**
 * One soft late pass for grids that inject cards after first paint.
 * Only runs if cards are still loading / missing data — never force-aborts a success.
 */
function scheduleLatePass() {
  window.setTimeout(() => {
    observeGrids();
    const ids = collectRenderedProductIds(document);
    if (!ids.length) return;

    const stillLoading = collectWeightCards(document).some((card) => {
      return card.dataset.minWeightState === 'loading' || !card.dataset.weightHydrated;
    });

    if (!stillLoading && lastAppliedRequestKey === createProductRequestKey(ids)) {
      return;
    }

    scheduleHydrate(document, { source: 'late-pass' });
  }, 1200);
}

function bindCartInventoryUpdates() {
  const availabilityApi = getWmsAvailabilityApi();
  const eventName =
    (availabilityApi && availabilityApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
  if (document.documentElement.dataset.aoneWeightCartBound === 'true') return;
  document.documentElement.dataset.aoneWeightCartBound = 'true';
  /** @type {number} */
  let lastCartMutationGeneration = 0;
  document.addEventListener(eventName, (event) => {
    const detail =
      event && /** @type {CustomEvent} */ (event).detail
        ? /** @type {{ mutationGeneration?: number, loading?: boolean, cartReady?: boolean }} */ (
            /** @type {CustomEvent} */ (event).detail
          )
        : null;
    const generation = Number(detail && detail.mutationGeneration);
    if (Number.isFinite(generation) && generation > 0) {
      if (generation < lastCartMutationGeneration) return;
      lastCartMutationGeneration = generation;
    }
    reapplyCartAwareCardDisplays(detail);
  });
}

function init() {
  if (initialized) return;
  initialized = true;

  ensureStore();
  ensureSharedWeightStore();

  const cfg = getWmsConfiguration();
  if (!cfg.enabled) {
    debugLog('Disabled via theme settings');
    return;
  }

  if (!cfg.baseUrl) {
    console.error(
      '[WMS availability] API base URL is missing. Configure it in Shopify Theme settings → WMS API Integration.'
    );
    return;
  }

  bindThemeEvents();
  bindCartInventoryUpdates();
  observeGrids();
  scheduleHydrate(document, { source: 'initial-page-load' });
  scheduleLatePass();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {
  hydrate,
  scheduleHydrate,
  formatWeight,
  waitForCurrentWeightData,
  loadCurrentProductWeights,
  normalizeShopifyId,
};

const api = {
  hydrate,
  scheduleHydrate,
  waitForCurrentWeightData,
  loadCurrentProductWeights,
  normalizeShopifyId,
  getStore: ensureStore,
};

(/** @type {any} */ (window)).AoneBatchWeight = api;
(/** @type {any} */ (window)).AOneProductWeightsAPI = api;
